import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "@/i18n/IntlProvider";
import { api } from "@/lib/api";

type ScanBucketResponse = {
  day: string;
  count: number;
};

type ScanTimeseriesResponse = {
  buckets: ScanBucketResponse[];
};

/**
 * Inline SVG bar-chart of the last `days` UTC scan-buckets. Stays
 * dependency-free on purpose — pulling in recharts/chart.js for one
 * sparkline isn't worth the bundle weight. Renders only for DYNAMIC,
 * non-archived codes; STATIC matrices have no redirect and therefore
 * no recorded scans.
 *
 * Empty-state and loading-state collapse to a short hint instead of an
 * empty chart-shaped placeholder, so the detail page doesn't look
 * broken on a freshly minted code.
 */
export function ScanTimeseriesCard({
  codeId,
  mode,
  days = 30,
}: {
  codeId: string;
  mode: "STATIC" | "DYNAMIC";
  days?: number;
}) {
  const t = useTranslations("app.codes.detail.scans");

  const query = useQuery({
    queryKey: ["code", codeId, "scans", "timeseries", days],
    queryFn: () =>
      api<ScanTimeseriesResponse>(`/v1/codes/${codeId}/scans/timeseries?days=${days}`),
    enabled: mode === "DYNAMIC",
    retry: false,
    staleTime: 60_000,
  });

  if (mode !== "DYNAMIC") {
    return (
      <section className="space-y-2 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold">{t("chartTitle")}</h2>
        <p className="text-xs text-muted-foreground">{t("chartStaticHint")}</p>
      </section>
    );
  }

  const buckets = query.data?.buckets ?? [];
  const max = buckets.reduce((acc, b) => (b.count > acc ? b.count : acc), 0);
  const total = buckets.reduce((acc, b) => acc + b.count, 0);

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold">{t("chartTitle")}</h2>
        {total > 0 ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {t("total", { count: total.toLocaleString() })}
          </span>
        ) : null}
      </header>

      {query.isLoading ? (
        <p className="text-xs text-muted-foreground">{t("loading")}</p>
      ) : total === 0 ? (
        <p className="text-xs text-muted-foreground">{t("chartEmpty")}</p>
      ) : (
        <BarRow buckets={buckets} max={max} />
      )}
    </section>
  );
}

/**
 * Bars laid out by CSS-grid so the column count exactly matches the
 * bucket count — bars are responsive to the card width without an
 * extra ResizeObserver. Each column is a flex-col with the count label
 * stacked on top of the bar (justify-end pins both to the bottom of
 * the cell, so the label sits just above the bar's top edge no matter
 * how tall the bar is). Bar height is computed in pixels against
 * `BAR_AREA_PX` so the count row always has its own reserved space —
 * mixing % heights with a sibling label produced a tiny vertical
 * jitter on the tallest day.
 */
function BarRow({
  buckets,
  max,
}: {
  buckets: ScanBucketResponse[];
  max: number;
}) {
  const BAR_AREA_PX = 110;
  return (
    <div className="space-y-2">
      <div
        className="grid gap-px"
        style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}
      >
        {buckets.map((b) => {
          const ratio = max > 0 ? b.count / max : 0;
          // Even days with 0 scans get a 1 px sliver so the axis stays
          // readable; the colour stays muted so it doesn't look like a
          // real bar.
          const heightPx = b.count === 0 ? 1 : Math.max(2, Math.round(ratio * BAR_AREA_PX));
          return (
            <div key={b.day} className="flex h-full flex-col items-stretch justify-end">
              {/* Reserve the label slot even on zero-days so every column
                  shares the same baseline; non-breaking space keeps the
                  span's intrinsic height = line-height. */}
              <span className="text-center text-[9px] leading-[12px] tabular-nums text-muted-foreground">
                {b.count > 0 ? b.count.toLocaleString() : " "}
              </span>
              <div
                className={
                  "mt-0.5 rounded-t-sm " +
                  (b.count === 0 ? "bg-muted" : "bg-primary/80 hover:bg-primary")
                }
                style={{ height: `${heightPx}px` }}
                title={`${b.day} — ${b.count.toLocaleString()}`}
              />
            </div>
          );
        })}
      </div>
      <div
        className="grid gap-px"
        style={{ gridTemplateColumns: `repeat(${buckets.length}, minmax(0, 1fr))` }}
      >
        {buckets.map((b) => (
          <span
            key={b.day}
            className="overflow-visible whitespace-nowrap text-center text-[10px] leading-tight tabular-nums text-muted-foreground"
          >
            {mondayLabel(b.day)}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Render a weekday + DD.MM label on Mondays, blank otherwise. Bars sit
 * on a 1-fr-per-day grid so anchoring the label to the bucket's column
 * keeps it visually centred under the Monday-bar; `whitespace-nowrap`
 * lets the text overflow its 1-column slot into the surrounding empty
 * cells without wrapping. Locale comes from the user's browser
 * (Intl default) — German installs see "Mo. 06.05.", English see
 * "Mon 05/06".
 */
function mondayLabel(isoDate: string): string {
  // Noon UTC dodges DST/midnight-rounding so getUTCDay() always reflects
  // the calendar day the bucket represents.
  const d = new Date(`${isoDate}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  if (d.getUTCDay() !== 1) return "";
  const weekday = d.toLocaleDateString(undefined, {
    weekday: "short",
    timeZone: "UTC",
  });
  const date = d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    timeZone: "UTC",
  });
  return `${weekday} ${date}`;
}
